// import components
import UnsavedDataAlert from 'components/shared/CRUD/components/InnerPage/components/UnsavedDataAlert';
import EditFooter from './components/EditFooter';
import ToolbarContent from './components/ToolbarContent';

// import hooks
import usePreviousLocation from './hooks/usePreviousLocation';

// import react hooks, components, and types
import { useEffect, useRef, useState } from 'react';

// import styles
import { StyledPaper } from 'components/shared/Paper/StyledPaper.styles';
import { StyledUnsavedDataAlertBox } from './EditPage.styles';

// import context
import { EditPageContext } from './EditPageContext';

// imports external libraries
import { AnimatePresence } from 'motion/react';
import { FormProvider } from 'react-hook-form';

// import types
import type { FC, PropsWithChildren } from 'react';
import type { EditPageProps } from './EditPage.types';

const EditPage: FC<PropsWithChildren<EditPageProps>> = ({
  children,
  withTabs = false,
  methods,
  onSubmit,
  toggleInnerLayout,
  onCancel,
  setCurrentView,
  views,
  currentView,
  isEditable,
  navigatorValueGetter,
  navigatorLabelGetter,
  onSetEditMode,
  onIsDirty
}) => {
  'use memo';

  const [isInEditMode, setIsInEditMode] = useState(false);
  const previousIsEditable = useRef(false);
  const { formState } = methods;

  const handleSetEditMode = (editMode: boolean) => {
    onSetEditMode && onSetEditMode(editMode);
    setIsInEditMode(editMode);
  };

  const handleCancel = () => {
    onCancel && onCancel();
    methods.reset(); // Reset form state
    handleSetEditMode(false);
  };

  useEffect(() => {
    onIsDirty && onIsDirty(formState.isDirty);
  }, [formState.isDirty, onIsDirty]);

  useEffect(() => {
    if (isEditable && previousIsEditable.current !== isEditable) {
      setIsInEditMode((previousEditMode) => {
        onSetEditMode?.(!previousEditMode);
        return !previousEditMode;
      });
    }

    previousIsEditable.current = Boolean(isEditable);
  }, [isEditable, onSetEditMode]);

  usePreviousLocation(toggleInnerLayout);

  return (
    <StyledPaper>
      <AnimatePresence mode="wait">
        {formState.isDirty && !withTabs && (
          <StyledUnsavedDataAlertBox
            key="motion-unsaved-edit-page"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <UnsavedDataAlert />
          </StyledUnsavedDataAlertBox>
        )}
      </AnimatePresence>
      <EditPageContext.Provider
        value={{
          isEditable: isInEditMode
        }}
      >
        <FormProvider {...methods}>
          <ToolbarContent
            setIsInEditMode={handleSetEditMode}
            isInEditMode={isInEditMode}
            toggleInnerLayout={toggleInnerLayout}
            setCurrentView={setCurrentView}
            views={views}
            currentView={currentView}
            navigatorValueGetter={navigatorValueGetter}
            navigatorLabelGetter={navigatorLabelGetter}
          />
          <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
            {children}
            {isInEditMode && (
              <EditFooter
                onCancelClick={handleCancel}
                submitDisabled={!formState.isDirty}
              />
            )}
          </form>
        </FormProvider>
      </EditPageContext.Provider>
    </StyledPaper>
  );
};

export default EditPage;
